import { api } from '@/services/api';

export type TemplateDomain = 'python' | 'information_systems' | 'networks' | 'iot';
export type TemplateTaskType = 'lesson_plan' | 'question_set' | 'topology' | 'slides' | 'mixed';
export type TemplateStatus = 'draft' | 'published' | 'archived';
export type GenerationJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';
export type LlmProviderType = 'openai_compatible';
export type LlmValidationStatus = 'unknown' | 'ok' | 'failed';
export type EffectiveTemplateSource = 'site_default' | 'user_override';
export type GenerationFeedbackRating = 'like' | 'dislike';

export type GenerationArtifactType =
  | 'lesson_plan_markdown'
  | 'assessment_set'
  | 'topology_spec'
  | 'slide_spec'
  | 'slides_html'
  | 'slides_beamer_tex';

export interface TemplateRecord {
  id: string;
  slug: string;
  name: string;
  description: string;
  domain: TemplateDomain;
  taskType: TemplateTaskType;
  status: TemplateStatus;
  inputSchemaJson: Record<string, unknown>;
  uiSchemaJson?: Record<string, unknown>;
  outputTargetsJson: GenerationArtifactType[];
  systemPrompt: string;
  planningPrompt: string;
  generationPrompt: string;
  tagsJson?: string[];
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateVersionRecord {
  id: string;
  templateId: string;
  version: number;
  snapshotJson: Record<string, unknown>;
  publishedAt: string;
  publishedById?: string;
}

export interface AdminTemplateDetail extends TemplateRecord {
  versions?: TemplateVersionRecord[];
}

export interface CreateTemplatePayload {
  slug: string;
  name: string;
  description: string;
  domain: TemplateDomain;
  taskType: TemplateTaskType;
  status?: TemplateStatus;
  inputSchemaJson: Record<string, unknown>;
  uiSchemaJson?: Record<string, unknown>;
  outputTargetsJson: GenerationArtifactType[];
  systemPrompt: string;
  planningPrompt: string;
  generationPrompt: string;
  tagsJson?: string[];
}

export type UpdateTemplatePayload = Partial<CreateTemplatePayload>;

export interface CompilePreviewPayload {
  systemPrompt: string;
  planningPrompt: string;
  generationPrompt: string;
  sampleInputJson: Record<string, unknown>;
  outputTargetsJson?: GenerationArtifactType[];
}

export interface CompiledPromptResult {
  resolvedSystemPrompt: string;
  resolvedPlanningPrompt: string;
  resolvedGenerationPrompt: string;
  targetArtifactTypes: GenerationArtifactType[];
  targetSchemaName: string;
}

export interface GenerationArtifactRecord {
  id: string;
  jobId: string;
  artifactType: GenerationArtifactType;
  mimeType: string;
  title: string;
  contentText?: string;
  contentJson?: Record<string, unknown>;
  metaJson?: Record<string, unknown>;
  createdAt: string;
}

export interface GenerationJobRecord {
  id: string;
  templateId: string;
  templateVersion: number;
  requestedById?: string;
  llmProviderId?: string;
  llmModelId?: string;
  templateOverrideId?: string;
  effectiveTemplateSource?: EffectiveTemplateSource;
  effectivePromptSnapshotJson?: Record<string, unknown>;
  status: GenerationJobStatus;
  inputJson: Record<string, unknown>;
  compiledPromptJson: CompiledPromptResult;
  rawLlmResponseText?: string;
  structuredOutputJson?: Record<string, unknown>;
  latencyMs?: number;
  tokenUsageJson?: Record<string, unknown>;
  errorText?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  template?: {
    id: string;
    slug: string;
    name: string;
    domain: TemplateDomain;
    taskType?: TemplateTaskType;
  };
  llmProvider?: {
    id: string;
    name: string;
    baseUrl?: string;
  } | null;
  llmModel?: {
    id: string;
    modelId: string;
    label: string;
  } | null;
  feedback?: GenerationFeedbackRecord[];
  artifacts?: GenerationArtifactRecord[];
}

export interface GenerateTemplateResponse {
  jobId: string;
  status: GenerationJobStatus;
  artifacts: GenerationArtifactRecord[];
  provider?: string;
  model?: string;
  templateSource?: EffectiveTemplateSource;
}

export interface LlmModelRecord {
  id: string;
  modelId: string;
  label: string;
  supportsStructuredOutput?: boolean | null;
  supportsReasoning?: boolean | null;
  metadataJson?: Record<string, unknown> | null;
  isEnabled: boolean;
  isDefault: boolean;
}

export interface UserLlmProviderRecord {
  id: string;
  providerType: LlmProviderType;
  name: string;
  baseUrl: string;
  enabled: boolean;
  isDefault: boolean;
  apiKeyLast4: string;
  apiKeyMasked: string;
  lastValidatedAt?: string | null;
  lastValidationStatus: LlmValidationStatus;
  lastValidationError?: string | null;
  modelCount: number;
  models: LlmModelRecord[];
  createdAt: string;
  updatedAt: string;
}

export interface DiscoveredLlmModel {
  modelId: string;
  label: string;
  supportsStructuredOutput?: boolean;
  supportsReasoning?: boolean;
  metadataJson?: Record<string, unknown>;
}

export interface ProviderValidationResponse {
  providerType: LlmProviderType;
  normalizedBaseUrl: string;
  status: LlmValidationStatus;
  listingSupported: boolean;
  error?: string;
  discoveredModels: DiscoveredLlmModel[];
}

export interface LlmProviderModelInput {
  modelId: string;
  label?: string;
  supportsStructuredOutput?: boolean;
  supportsReasoning?: boolean;
  metadataJson?: Record<string, unknown>;
}

export interface CreateLlmProviderPayload {
  providerType: LlmProviderType;
  name: string;
  baseUrl: string;
  apiKey: string;
  enabled?: boolean;
  isDefault?: boolean;
  importModels?: boolean;
  selectedModels?: LlmProviderModelInput[];
}

export interface UpdateLlmProviderPayload {
  name?: string;
  baseUrl?: string;
  apiKey?: string;
  enabled?: boolean;
  isDefault?: boolean;
}

export interface AddLlmProviderModelPayload extends LlmProviderModelInput {
  isEnabled?: boolean;
  isDefault?: boolean;
}

export interface UpdateLlmProviderModelPayload {
  isEnabled?: boolean;
  isDefault?: boolean;
  label?: string;
}

export interface UserTemplateOverrideRecord {
  id: string;
  userId: string;
  templateId: string;
  baseTemplateVersion: number;
  name?: string | null;
  systemPromptOverride?: string | null;
  planningPromptOverride?: string | null;
  generationPromptOverride?: string | null;
  outputTargetsOverrideJson?: GenerationArtifactType[] | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateOverrideSourceTemplate {
  id: string;
  slug: string;
  name: string;
  domain: TemplateDomain;
  taskType: TemplateTaskType;
  status: TemplateStatus;
}

export interface MyTemplateOverrideListItem extends UserTemplateOverrideRecord {
  template: TemplateOverrideSourceTemplate | null;
}

export interface MyTemplateOverrideResponse {
  templateId: string;
  baseTemplateVersion: number;
  source: EffectiveTemplateSource;
  override: UserTemplateOverrideRecord | null;
  effectivePrompt: Record<string, unknown>;
}

export interface UpsertMyTemplateOverridePayload {
  name?: string;
  systemPromptOverride?: string;
  planningPromptOverride?: string;
  generationPromptOverride?: string;
  outputTargetsOverrideJson?: GenerationArtifactType[];
  isActive?: boolean;
}

export interface CreateMyTemplateOverridePayload {
  name: string;
  systemPromptOverride?: string;
  planningPromptOverride?: string;
  generationPromptOverride?: string;
  outputTargetsOverrideJson?: GenerationArtifactType[];
  isActive?: boolean;
}

export interface GenerationFeedbackRecord {
  id: string;
  generationJobId: string;
  userId: string;
  rating: GenerationFeedbackRating;
  feedbackText?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertGenerationFeedbackPayload {
  rating: GenerationFeedbackRating;
  feedbackText?: string;
}

export interface AdminFeedbackSummary {
  totalGenerations: number;
  totalFeedback: number;
  likeCount: number;
  dislikeCount: number;
  likeRatio: number;
  mostLikedTemplates: Array<{ templateId: string; templateName: string; count: number }>;
  mostDislikedTemplates: Array<{ templateId: string; templateName: string; count: number }>;
  feedbackByDomain: Array<{ key: string; count: number }>;
  feedbackByProviderModel: Array<{ key: string; count: number }>;
  feedbackOverTime: Array<{ key: string; count: number }>;
}

export interface AdminFeedbackListResponse {
  page: number;
  pageSize: number;
  total: number;
  items: Array<{
    id: string;
    createdAt: string;
    user: { id: string; name: string; email: string } | null;
    generationJobId: string;
    templateId: string;
    templateName: string;
    templateVersion: number;
    domain: string;
    providerId: string;
    providerName: string;
    modelId: string;
    modelRowId: string;
    rating: GenerationFeedbackRating;
    feedbackText: string;
    effectivePromptSnapshotJson: Record<string, unknown> | null;
    inputJson: Record<string, unknown> | null;
    structuredOutputJson: Record<string, unknown> | null;
  }>;
}

interface AdminTemplateListParams {
  domain?: TemplateDomain;
  status?: TemplateStatus;
  taskType?: TemplateTaskType;
  keyword?: string;
}

interface PublishedTemplateListParams {
  domain?: TemplateDomain;
  taskType?: TemplateTaskType;
}

export async function listAdminTemplates(params: AdminTemplateListParams = {}) {
  const response = await api.get<TemplateRecord[]>('/admin/templates', { params });
  return response.data;
}

export async function getAdminTemplate(templateId: string) {
  const response = await api.get<AdminTemplateDetail>(`/admin/templates/${templateId}`);
  return response.data;
}

export async function createAdminTemplate(payload: CreateTemplatePayload) {
  const response = await api.post<TemplateRecord>('/admin/templates', payload);
  return response.data;
}

export async function updateAdminTemplate(templateId: string, payload: UpdateTemplatePayload) {
  const response = await api.patch<TemplateRecord>(`/admin/templates/${templateId}`, payload);
  return response.data;
}

export async function publishAdminTemplate(templateId: string) {
  const response = await api.post<{ template: TemplateRecord; version: TemplateVersionRecord }>(
    `/admin/templates/${templateId}/publish`,
  );
  return response.data;
}

export async function archiveAdminTemplate(templateId: string) {
  const response = await api.post<TemplateRecord>(`/admin/templates/${templateId}/archive`);
  return response.data;
}

export async function previewCompileTemplate(payload: CompilePreviewPayload) {
  const response = await api.post<CompiledPromptResult>('/admin/templates/preview-compile', payload);
  return response.data;
}

export async function listPublishedTemplates(params: PublishedTemplateListParams = {}) {
  const response = await api.get<TemplateRecord[]>('/templates/published', { params });
  return response.data;
}

export async function getPublishedTemplate(templateId: string) {
  const response = await api.get<TemplateRecord>(`/templates/published/${templateId}`);
  return response.data;
}

export async function generateFromTemplate(
  templateId: string,
  input: Record<string, unknown>,
  options?: {
    providerId?: string;
    modelId?: string;
    templateOverrideId?: string;
  },
) {
  const response = await api.post<GenerateTemplateResponse>(`/templates/${templateId}/generate`, {
    input,
    providerId: options?.providerId,
    modelId: options?.modelId,
    templateOverrideId: options?.templateOverrideId,
  });
  return response.data;
}

export async function listGenerationJobs() {
  const response = await api.get<GenerationJobRecord[]>('/generation-jobs');
  return response.data;
}

export async function getGenerationJob(jobId: string) {
  const response = await api.get<GenerationJobRecord>(`/generation-jobs/${jobId}`);
  return response.data;
}

export async function listGenerationArtifacts(jobId: string) {
  const response = await api.get<GenerationArtifactRecord[]>(`/generation-jobs/${jobId}/artifacts`);
  return response.data;
}

export async function getGenerationArtifact(jobId: string, artifactId: string) {
  const response = await api.get<GenerationArtifactRecord>(`/generation-jobs/${jobId}/artifacts/${artifactId}`);
  return response.data;
}

export async function listUserLlmProviders() {
  const response = await api.get<UserLlmProviderRecord[]>('/llm/providers');
  return response.data;
}

export async function validateUserLlmProvider(payload: {
  providerType: LlmProviderType;
  name: string;
  baseUrl: string;
  apiKey: string;
}) {
  const response = await api.post<ProviderValidationResponse>('/llm/providers/validate', payload);
  return response.data;
}

export async function createUserLlmProvider(payload: CreateLlmProviderPayload) {
  const response = await api.post<UserLlmProviderRecord>('/llm/providers', payload);
  return response.data;
}

export async function updateUserLlmProvider(providerId: string, payload: UpdateLlmProviderPayload) {
  const response = await api.patch<UserLlmProviderRecord>(`/llm/providers/${providerId}`, payload);
  return response.data;
}

export async function refreshUserLlmProviderModels(providerId: string) {
  const response = await api.post<{
    provider: UserLlmProviderRecord;
    discoveredModels: DiscoveredLlmModel[];
    listingSupported: boolean;
  }>(`/llm/providers/${providerId}/refresh-models`);
  return response.data;
}

export async function addUserLlmProviderModel(providerId: string, payload: AddLlmProviderModelPayload) {
  const response = await api.post<UserLlmProviderRecord>(`/llm/providers/${providerId}/models`, payload);
  return response.data;
}

export async function updateUserLlmProviderModel(
  providerId: string,
  modelId: string,
  payload: UpdateLlmProviderModelPayload,
) {
  const response = await api.patch<UserLlmProviderRecord>(`/llm/providers/${providerId}/models/${modelId}`, payload);
  return response.data;
}

export async function deleteUserLlmProvider(providerId: string) {
  const response = await api.delete<{ deleted: boolean }>(`/llm/providers/${providerId}`);
  return response.data;
}

export async function getMyTemplateOverride(templateId: string) {
  const response = await api.get<MyTemplateOverrideResponse>(`/templates/${templateId}/my-override`);
  return response.data;
}

export async function upsertMyTemplateOverride(templateId: string, payload: UpsertMyTemplateOverridePayload) {
  const response = await api.post<UserTemplateOverrideRecord>(`/templates/${templateId}/my-override`, payload);
  return response.data;
}

export async function createMyTemplateOverride(templateId: string, payload: CreateMyTemplateOverridePayload) {
  const response = await api.post<UserTemplateOverrideRecord>(`/templates/${templateId}/custom-templates`, payload);
  return response.data;
}

export async function listMyTemplateOverrides(params: { templateId?: string } = {}) {
  const response = await api.get<MyTemplateOverrideListItem[]>('/template-overrides', { params });
  return response.data;
}

export async function patchTemplateOverride(overrideId: string, payload: UpsertMyTemplateOverridePayload) {
  const response = await api.patch<UserTemplateOverrideRecord>(`/template-overrides/${overrideId}`, payload);
  return response.data;
}

export async function deleteTemplateOverride(overrideId: string) {
  const response = await api.delete<{ deleted: boolean }>(`/template-overrides/${overrideId}`);
  return response.data;
}

export async function getGenerationFeedback(jobId: string) {
  const response = await api.get<GenerationFeedbackRecord | null>(`/generation-jobs/${jobId}/feedback`);
  return response.data;
}

export async function upsertGenerationFeedback(jobId: string, payload: UpsertGenerationFeedbackPayload) {
  const response = await api.post<GenerationFeedbackRecord>(`/generation-jobs/${jobId}/feedback`, payload);
  return response.data;
}

export async function getAdminGenerationFeedbackSummary(params: Record<string, unknown> = {}) {
  const response = await api.get<AdminFeedbackSummary>('/admin/generation-feedback/summary', { params });
  return response.data;
}

export async function listAdminGenerationFeedback(params: Record<string, unknown> = {}) {
  const response = await api.get<AdminFeedbackListResponse>('/admin/generation-feedback', { params });
  return response.data;
}

export async function exportAdminGenerationFeedback(params: { format?: 'json' | 'csv' } & Record<string, unknown> = {}) {
  const format = params.format ?? 'json';
  const response = await api.get<string | object>('/admin/generation-feedback/export', {
    params,
    responseType: format === 'csv' ? 'text' : 'json',
  });
  return response.data;
}
